// JadEye API Library
// 통합 API 호출 라이브러리
import { apiLogger } from './logger';

export interface ApiOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    data?: any;
    headers?: Record<string, string>;
    timeout?: number;
    onSuccess?: (data: any) => void;
    onError?: (error: any, errorData?: any) => void;
    onFinally?: () => void;
    loading?: { set?: (loading: boolean) => void };
}

export interface ApiResponse<T = any> {
    data: T;
    status: number;
    message?: string;
    success: boolean;
}

class JadEyeAPI {
    private baseURL: string;
    private defaultHeaders: Record<string, string>;

    constructor() {
        this.baseURL = '';
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };

        // CSRF 토큰 자동 추가
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
            this.defaultHeaders['X-CSRF-TOKEN'] = csrfToken;
        }
    }

    /**
     * Laravel 응답 데이터를 표준화 (이중 data 구조 자동 처리)
     */
    private normalizeResponseData(responseData: any): any {
        // 텍스트 응답인 경우 그대로 반환
        if (typeof responseData !== 'object' || responseData === null) {
            return responseData;
        }

        // 배열이 직접 온 경우 (예: vital-signs API)
        if (Array.isArray(responseData)) {
            return responseData;
        }

        // ✅ BaseResponseController 표준 응답 처리
        if (responseData.data_type) {
            switch (responseData.data_type) {
                case 'array':
                case 'collection':
                case 'object':
                    // array, collection, object는 data만 추출
                    return responseData.data;
                case 'paginated':
                    // paginated는 전체 객체 반환 (data + pagination 필요)
                    return responseData;
                default:
                    // 알 수 없는 data_type은 전체 반환
                    return responseData;
            }
        }

        // Laravel 이중 data 구조인 경우: {data: {data: actualData}}
        if (responseData.data && typeof responseData.data === 'object' && 'data' in responseData.data) {
            return responseData.data;
        }

        // Laravel 단일 data 구조인 경우: {data: actualData}
        if (responseData.data !== undefined) {
            return responseData.data;
        }

        // 그 외의 경우 원본 반환
        return responseData;
    }

    /**
     * Firebase ID Token을 Authorization 헤더에 추가
     */
    private async addFirebaseToken(headers: Record<string, string>) {
        try {
            // Firebase 앱이 초기화되었는지 먼저 확인
            const { getApps } = await import('firebase/app');
            const { getAuth } = await import('firebase/auth');
            const apps = getApps();
            
            // Firebase 앱이 초기화되지 않았으면 토큰 추가 생략
            if (apps.length === 0) {
                return;
            }
            
            const auth = getAuth();
            
            if (auth.currentUser) {
                const idToken = await auth.currentUser.getIdToken();
                headers['Authorization'] = `Bearer ${idToken}`;
            }
        } catch (error) {
            // Firebase 관련 오류는 조용히 처리 (API 호출은 계속 진행)
            apiLogger.warn('Firebase token 추가 실패:', error);
        }
    }

    /**
     * 통합 API 호출 메서드 - 메모리 최적화
     * 개발자는 이것만 알면 됨!
     */
    async call<T = any>(url: string, options: ApiOptions = {}): Promise<ApiResponse<T>> {
        const {
            method = 'GET',
            data,
            headers = {},
            timeout = 10000,
            onSuccess,
            onError,
            onFinally,
            loading
        } = options;

        // 로딩 상태 시작
        loading?.set?.(true);

        // 메모리 정리를 위한 변수들
        let controller: AbortController | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        try {
            controller = new AbortController();
            timeoutId = setTimeout(() => {
                controller?.abort();
                // 메모리 정리
                controller = null;
                timeoutId = null;
            }, timeout);

            // Firebase ID Token 자동 추가
            const requestHeaders = { ...this.defaultHeaders, ...headers };
            await this.addFirebaseToken(requestHeaders);

            const fetchOptions: RequestInit = {
                method,
                headers: requestHeaders,
                signal: controller.signal,
                credentials: 'same-origin', // Laravel 세션 쿠키 자동 포함
            };

            // POST, PUT, PATCH일 때만 body 추가
            if (['POST', 'PUT', 'PATCH'].includes(method) && data) {
                fetchOptions.body = JSON.stringify(data);
            }

            const response = await fetch(this.baseURL + url, fetchOptions);
            
            // 즉시 타이머 정리
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            let responseData;
            const contentType = response.headers.get('content-type');

            if (contentType?.includes('application/json')) {
                responseData = await response.json();
            } else {
                responseData = await response.text();
            }


            // Laravel 이중 data 구조를 자동으로 표준화
            const normalizedData = this.normalizeResponseData(responseData);

            const apiResponse: ApiResponse<T> = {
                data: normalizedData,
                status: response.status,
                success: response.ok,
                message: responseData?.message || (response.ok ? 'Success' : 'Error')
            };

            if (response.ok) {
                onSuccess?.(apiResponse.data);
                return apiResponse;
            } else {
                // Laravel validation 오류 등을 onError에 전달
                onError?.(new Error(apiResponse.message), responseData);
                throw new Error(apiResponse.message);
            }

        } catch (error: any) {
            // 메모리 정리
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            controller = null;

            // AbortError는 정상적인 취소 신호
            if (error.name === 'AbortError' || error.message?.includes('aborted')) {
                // AbortError는 던지지 않고 조용히 처리
                apiLogger.log('API 요청이 취소되었습니다:', url);
                
                // AbortError 시 적절한 응답 반환
                const abortResponse: ApiResponse<T> = {
                    data: null as T,
                    status: 0,
                    success: false,
                    message: 'Request aborted'
                };
                return abortResponse;
            }

            const errorResponse: ApiResponse<T> = {
                data: null as T,
                status: 0,
                success: false,
                message: error.message || 'Network Error'
            };

            // catch 블록에서는 error만 전달 (네트워크 오류 등)
            onError?.(error);
            throw errorResponse;

        } finally {
            // 최종 메모리 정리
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            controller = null;
            
            loading?.set?.(false);
            onFinally?.();
        }
    }

    // 편의 메서드들
    async get<T = any>(url: string, options: Omit<ApiOptions, 'method'> = {}) {
        return this.call<T>(url, { ...options, method: 'GET' });
    }

    async post<T = any>(url: string, data?: any, options: Omit<ApiOptions, 'method' | 'data'> = {}) {
        return this.call<T>(url, { ...options, method: 'POST', data });
    }

    async put<T = any>(url: string, data?: any, options: Omit<ApiOptions, 'method' | 'data'> = {}) {
        return this.call<T>(url, { ...options, method: 'PUT', data });
    }

    async patch<T = any>(url: string, data?: any, options: Omit<ApiOptions, 'method' | 'data'> = {}) {
        return this.call<T>(url, { ...options, method: 'PATCH', data });
    }

    async delete<T = any>(url: string, options: Omit<ApiOptions, 'method'> = {}) {
        return this.call<T>(url, { ...options, method: 'DELETE' });
    }

    // 설정 메서드들
    setBaseURL(url: string) {
        this.baseURL = url;
        return this;
    }

    setDefaultHeaders(headers: Record<string, string>) {
        this.defaultHeaders = { ...this.defaultHeaders, ...headers };
        return this;
    }
}


// 싱글톤 인스턴스
export const api = new JadEyeAPI();

// 기본 export
export default api;
